import React, { useState, useEffect } from 'react';
import { Alert, AlertTitle, Snackbar, Slide } from '@mui/material';
import PropTypes from 'prop-types';

/**
 * Generic Alert component that can be used throughout the application
 * @param {Object} props - Component props
 * @param {string} props.message - Alert message to display
 * @param {string} props.type - Alert type (success, error, warning, info)
 * @param {string} [props.title] - Optional title for the alert
 * @param {boolean} [props.open] - Whether the alert is visible
 * @param {Function} [props.onClose] - Callback function when alert is closed
 * @param {boolean} [props.autoHide] - Whether to auto-hide the alert
 * @param {number} [props.duration] - Duration in ms before auto-hiding (default: 6000)
 * @param {Object} [props.sx] - Additional styles
 * @returns {JSX.Element|null} The Alert component
 */
const AlertComponent = ({
  message,
  type = 'info',
  title,
  open: propOpen = true,
  onClose,
  autoHide = true,
  duration = 6000,
  sx = {},
}) => {
  const [open, setOpen] = useState(propOpen);
  
  // Handle controlled open prop
  useEffect(() => {
    setOpen(propOpen);
  }, [propOpen]);

  // Handle auto-hide
  useEffect(() => {
    if (autoHide && open) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);
      
      return () => clearTimeout(timer);
    }
  }, [autoHide, duration, open]);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') return;
    
    setOpen(false);
    
    if (onClose) {
      onClose();
    }
  };

return (
    <Snackbar
        open={open}
        autoHideDuration={autoHide ? duration : null}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        TransitionComponent={Slide}
    >
        <Alert 
            onClose={handleClose} 
            severity={type} 
            variant="filled"
            sx={{ 
                width: '100%', 
                minWidth: '500px', 
                maxWidth: '80vw',  
                boxShadow: 3,
                ...sx 
            }}
        >
            {title && <AlertTitle>{title}</AlertTitle>}
            {message}
        </Alert>
    </Snackbar>
);
};

AlertComponent.propTypes = {
  message: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['success', 'error', 'warning', 'info']),
  title: PropTypes.string,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  autoHide: PropTypes.bool,
  duration: PropTypes.number,
  sx: PropTypes.object,
};

export default AlertComponent;